import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import analytics from '../services/analytics';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Hook personalizado para Google Analytics
 * Fornece tracking automático e funções de conveniência
 */
export const useAnalytics = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { language } = useLanguage();

  // Tracking automático de mudanças de página (otimizado)
  useEffect(() => {
    const pageName = getPageName(location.pathname);
    
    analytics.trackPageView(pageName, {
      path: location.pathname,
      search: location.search,
      hash: location.hash,
      user_authenticated: !!user,
      language: language
    });
  }, [location.pathname, location.search, location.hash]); // Removido user e language para evitar múltiplos disparos

  // Identifica usuário quando faz login (otimizado para evitar re-identificação desnecessária)
  useEffect(() => {
    if (user) {
      analytics.identifyUser(user.id, {
        user_type: user.user_metadata?.user_type || 'free',
        preferred_language: language,
        registration_date: user.created_at
      });
    }
  }, [user?.id, language]); // Só re-identificar se o ID do usuário ou idioma mudarem

  // Funções de conveniência para componentes
  const trackEvent = useCallback((action: string, category: string, label?: string, value?: number) => {
    analytics.trackEvent({
      action,
      category,
      label,
      value,
      custom_parameters: {
        page: location.pathname,
        user_id: user?.id,
        language
      }
    });
  }, [location.pathname, user?.id, language]);

  const trackClick = useCallback((elementName: string, context?: string) => {
    trackEvent('click', 'interaction', elementName);
    
    if (context) {
      analytics.trackEvent({
        action: 'element_click',
        category: 'ui_interaction',
        label: elementName,
        custom_parameters: {
          context,
          page: location.pathname
        }
      });
    }
  }, [trackEvent, location.pathname]);

  const trackFormSubmit = useCallback((formName: string, success: boolean, errorMessage?: string) => {
    analytics.trackEvent({
      action: success ? 'form_submit_success' : 'form_submit_error',
      category: 'form_interaction',
      label: formName,
      custom_parameters: {
        form_name: formName,
        success,
        error_message: errorMessage,
        page: location.pathname
      }
    });
  }, [location.pathname]);

  const trackSearch = useCallback((searchTerm: string, resultsCount: number, context: string = 'general') => {
    analytics.trackEvent({
      action: 'search',
      category: 'interaction',
      label: searchTerm,
      value: resultsCount,
      custom_parameters: {
        search_term: searchTerm,
        results_count: resultsCount,
        search_context: context,
        page: location.pathname
      }
    });
  }, [location.pathname]);

  const trackError = useCallback((errorType: string, errorMessage: string, fatal: boolean = false) => {
    analytics.trackException(`${errorType}: ${errorMessage}`, fatal);
    analytics.trackBusiness.errorEncounter(errorType, errorMessage, location.pathname);
  }, [location.pathname]);

  const trackPerformance = useCallback((metricName: string, value: number) => {
    analytics.trackTiming(metricName, value, 'user_experience');
  }, []);

  return {
    // Serviço completo
    analytics,
    
    // Funções de conveniência
    trackEvent,
    trackClick,
    trackFormSubmit,
    trackSearch,
    trackError,
    trackPerformance,
    
    // Tracking específico de negócio
    trackAuth: analytics.trackAuth,
    trackExam: analytics.trackExam,
    trackNavigation: analytics.trackNavigation,
    trackBusiness: analytics.trackBusiness,
    
    // Utilidades
    setUserProperties: analytics.setUserProperties,
    identifyUser: analytics.identifyUser,
    trackConversion: analytics.trackConversion
  };
};

/**
 * Mapeia rotas para nomes de página legíveis
 */
function getPageName(pathname: string): string {
  const routes: Record<string, string> = {
    '/': 'Landing Page',
    '/auth': 'Authentication',
    '/dashboard': 'Dashboard',
    '/exams': 'Exam Catalog',
    '/history': 'Exam History',
    '/profile': 'User Profile',
    '/admin': 'Admin Panel'
  };

  // Rotas dinâmicas
  if (pathname.startsWith('/exam/')) {
    return 'Exam Taking';
  }
  if (pathname.startsWith('/vendor/')) {
    return 'Vendor Details';
  }
  if (pathname.startsWith('/results/')) {
    return 'Exam Results';
  }

  return routes[pathname] || `Page: ${pathname}`;
}

export default useAnalytics;
